import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { login as loginRequest, fetchUserProfile } from "../services/authService";
import { setTokenGetter, ApiError } from "../services/apiClient";
import type { Session, UserProfile, AuthRawResponse, AuthStatus } from "../types/auth.types";

// Pick the active role for a set of roles. Prefer `landlord` when present
// (dual-role users land on the landlord dashboard by default), fall back to the
// previous selection if still valid, then the first role, then `tenant`.
const computeCurrentRole = (roles: string[], previous?: string | null): string => {
  const list = Array.isArray(roles) ? roles : [];
  if (previous && list.includes(previous)) return previous;
  if (list.includes("landlord")) return "landlord";
  if (list.length) return list[0];
  return "tenant";
};

// Build a consistent session shape from login/register responses.
// Login returns { jwt, id, roles }; register embeds a full `user` object (now
// with `roles`). When the profile isn't embedded we fetch GET /users/:id, which
// also returns `roles`, so the role set is always authoritative (not stale JWT).
const normalizeSession = async (rawResponse: AuthRawResponse): Promise<Session> => {
  const userId = rawResponse.id ?? rawResponse.user?.id;
  const jwt = rawResponse.jwt;
  const embeddedProfile = rawResponse.user ?? {};
  const needsFetch = !embeddedProfile.email && userId && jwt;

  let profile: UserProfile = embeddedProfile;
  if (needsFetch) {
    try {
      profile = await fetchUserProfile(userId as number | string, jwt as string);
    } catch {
      profile = {};
    }
  }

  const roles =
    rawResponse.roles ?? profile.roles ?? embeddedProfile.roles ?? [];

  return {
    jwt,
    id: userId,
    email: profile.email ?? rawResponse.email ?? "",
    username: profile.username ?? rawResponse.username ?? "",
    firstName: profile.first_name ?? "",
    lastName: profile.last_name ?? "",
    confirmedAt: profile.confirmed_at ?? rawResponse.confirmed_at ?? null,
    roles: Array.isArray(roles) ? roles : [],
  };
};

// "Keep me signed in": persistence goes to localStorage (checked) or
// sessionStorage (unchecked). `storageMode` is set BEFORE setSession so the
// persist middleware writes to the correct store. On write we also clear the
// other store to prevent a stale session resurrecting on reload.
let storageMode: string = "local";
const pickStorage = (): Storage =>
  storageMode === "session" ? window.sessionStorage : window.localStorage;
const otherStorage = (): Storage =>
  storageMode === "session" ? window.localStorage : window.sessionStorage;

const storageProxy = {
  getItem: (name: string): string | null =>
    window.localStorage.getItem(name) ?? window.sessionStorage.getItem(name),
  setItem: (name: string, value: string): void => {
    pickStorage().setItem(name, value);
    otherStorage().removeItem(name);
  },
  removeItem: (name: string): void => {
    window.localStorage.removeItem(name);
    window.sessionStorage.removeItem(name);
  },
};

const DEFAULT_SESSION: Session | null = null;

// Client-side JWT expiry check so boot validation can skip a doomed request
// (and log out instantly, even offline). 30s leeway for clock skew. Returns
// false for malformed tokens so the API still gets the final say.
const isJwtExpired = (jwt: string): boolean => {
  try {
    const [, payload] = jwt.split(".");
    const { exp } = JSON.parse(atob(payload)) as { exp?: number };
    if (typeof exp !== "number") return false;
    return exp * 1000 <= Date.now() + 30_000;
  } catch {
    return false;
  }
};

interface AuthState {
  session: Session | null;
  roles: string[];
  currentRole: string | null;
  // Boot verification lifecycle. Never persisted (partialize excludes it), so
  // every load starts at "unknown" and a stale stored JWT is verified before
  // trusted UI renders.
  status: AuthStatus;

  // Authenticate the user. Returns the normalized session on success;
  // throws on failure so the caller can render errors + handle navigation.
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<Session>;

  logout: () => void;

  // Complete signup: the register endpoint already returns a JWT + profile, so
  // normalize it into a session and drop the user straight into their account
  // (no separate login step).
  register: (rawResponse: AuthRawResponse) => Promise<Session>;

  // Switch the active role context (validated against the held roles).
  setCurrentRole: (role: string) => void;

  // Re-authoritative roles from the API so a stale JWT (60-day exp) can't
  // lock the UI into removed roles. Called once on app boot.
  refreshRoles: () => Promise<void>;

  // Validate the persisted session once at boot, silently: no navigation side
  // effects whatever the outcome. Returns true when the session is usable.
  // - No stored JWT → guest, no request.
  // - Expired JWT (client exp check) → silent logout, no request.
  // - 401/403/404 from the API → silent logout (dead token/account).
  // - Anything else (offline, 5xx) → keep the session (matches the offline
  //   behavior the app already has); real 401s later still bounce as usual.
  validateSession: () => Promise<boolean>;

  // Re-fetch the current user's profile and rebuild the session (incl.
  // confirmedAt) so confirming an email (or other profile changes) is reflected
  // live without a logout/login round-trip.
  refreshSession: () => Promise<void>;

  // Preserves the legacy API used by signUpForm: `setUser({ jwt, id })`.
  setUser: (value: Session | null) => void;
  setSession: (value: Session | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: DEFAULT_SESSION,
      roles: [],
      currentRole: null,
      status: "unknown",

      login: async (email, password, rememberMe = false) => {
        storageMode = rememberMe ? "local" : "session";
        const raw = await loginRequest(email, password);
        const session = await normalizeSession(raw);
        const currentRole = computeCurrentRole(session.roles);
        set({ session, roles: session.roles, currentRole, status: "authed" });
        return session;
      },

      logout: () => {
        set({ session: DEFAULT_SESSION, roles: [], currentRole: null, status: "guest" });
      },

      register: async (rawResponse) => {
        const session = await normalizeSession(rawResponse);
        const currentRole = computeCurrentRole(session.roles);
        set({ session, roles: session.roles, currentRole, status: "authed" });
        return session;
      },

      setCurrentRole: (role) => {
        const roles = get().roles || [];
        if (roles.includes(role)) set({ currentRole: role });
      },

      refreshRoles: async () => {
        const { session } = get();
        if (!session?.jwt || !session?.id) return;
        try {
          const profile = await fetchUserProfile(session.id, session.jwt);
          const roles = Array.isArray(profile.roles)
            ? profile.roles
            : get().roles || [];
          const currentRole = computeCurrentRole(roles, get().currentRole);
          set({ roles, currentRole });
        } catch {
          // Keep the persisted roles if the refresh fails (offline, 401, etc.).
        }
      },

      refreshSession: async () => {
        const { session } = get();
        if (!session?.jwt || !session?.id) return;
        try {
          const profile = await fetchUserProfile(session.id, session.jwt);
          const refreshed = await normalizeSession({
            id: session.id,
            jwt: session.jwt,
            user: profile,
          });
          set({
            session: refreshed,
            roles: refreshed.roles,
            currentRole: computeCurrentRole(refreshed.roles, get().currentRole),
          });
        } catch {
          // Keep the existing session if the refresh fails (offline, 401, etc.).
        }
      },

      validateSession: async () => {
        const { session } = get();
        if (!session?.jwt || !session?.id) {
          set({ status: "guest" });
          return false;
        }
        if (isJwtExpired(session.jwt)) {
          get().logout();
          return false;
        }
        set({ status: "verifying" });
        try {
          const profile = await fetchUserProfile(session.id, session.jwt, {
            silent: true,
          });
          const roles = Array.isArray(profile.roles)
            ? profile.roles
            : get().roles || [];
          const currentRole = computeCurrentRole(roles, get().currentRole);
          set({ roles, currentRole, status: "authed" });
          return true;
        } catch (error) {
          const httpStatus =
            error instanceof ApiError ? error.status : undefined;
          if (
            httpStatus === 401 ||
            httpStatus === 403 ||
            httpStatus === 404
          ) {
            // Dead token or dead account: clear silently. No navigation here —
            // the router gate + protected-route guards handle what's next.
            get().logout();
            return false;
          }
          // Unreachable API etc: keep the persisted session (matches the
          // offline behavior the app already has).
          set({ status: "authed" });
          return true;
        }
      },

      setUser: (value) => set({ session: value }),
      setSession: (value) => set({ session: value }),
    }),
    {
      name: "auth",
      storage: createJSONStorage(() => storageProxy),
      partialize: (state) => ({
        session: state.session,
        roles: state.roles,
        currentRole: state.currentRole,
      }),
    }
  )
);

// Let apiClient read the current JWT without importing the store (no cycle).
setTokenGetter(() => useAuthStore.getState().session?.jwt);
